(() => {
  const ROOT_SELECTOR = '[data-rplus]';
  const MOBILE_QUERY = '(max-width: 749px)';

  const parseBoolean = (value) => value === true || value === 'true';

  class RevibrasPlus {
    constructor(root) {
      if (!root || root.dataset.rplusReady === 'true') return;

      this.root = root;
      this.root.dataset.rplusReady = 'true';
      this.hero = root.querySelector('[data-rplus-hero]');
      this.buy = root.querySelector('[data-rplus-buy]');
      this.sticky = root.querySelector('[data-rplus-sticky]');
      this.stickyStop = root.querySelector('[data-rplus-sticky-stop]');
      this.variantSelects = [...root.querySelectorAll('[data-rplus-variant-select]')];
      this.variants = this.readVariants();
      this.mobileMedia = window.matchMedia(MOBILE_QUERY);
      this.heroVisible = true;
      this.stopVisible = false;

      this.bindVariantSelectors();
      this.bindBuyLinks();
      this.setupSticky();
    }

    readVariants() {
      const source = this.root.querySelector('[data-rplus-variants]');
      if (!source) return [];

      try {
        const variants = JSON.parse(source.textContent);
        return Array.isArray(variants) ? variants : [];
      } catch (error) {
        console.warn('REVIBRAS+: no fue posible leer las variantes.', error);
        return [];
      }
    }

    bindVariantSelectors() {
      this.variantSelects.forEach((select) => {
        select.addEventListener('change', () => this.updateVariant(select));
      });

      if (this.variantSelects[0]) this.updateVariant(this.variantSelects[0]);
    }

    getSelectedVariant(select) {
      const option = select.options[select.selectedIndex];
      if (!option) return null;

      const variantData = this.variants.find((variant) => String(variant.id) === String(option.value)) || {};

      return {
        id: option.value,
        title: variantData.title || option.textContent.trim(),
        price: option.dataset.price || variantData.formattedPrice || '',
        comparePrice: option.dataset.comparePrice || variantData.formattedCompareAtPrice || '',
        compareAtPrice: Number(variantData.compareAtPrice || 0),
        rawPrice: Number(variantData.price || 0),
        available: option.dataset.available
          ? parseBoolean(option.dataset.available)
          : parseBoolean(variantData.available),
        image: option.dataset.image || variantData.image || '',
        imageAlt: option.dataset.imageAlt || variantData.imageAlt || option.textContent.trim(),
      };
    }

    updateVariant(sourceSelect) {
      const variant = this.getSelectedVariant(sourceSelect);
      if (!variant) return;

      this.variantSelects.forEach((select) => {
        if (select !== sourceSelect && select.value !== variant.id) select.value = variant.id;
      });

      this.root.querySelectorAll('[data-rplus-variant-id]').forEach((input) => {
        input.value = variant.id;
        input.disabled = !variant.available;
      });

      this.root.querySelectorAll('[data-rplus-price], [data-rplus-sticky-price]').forEach((price) => {
        if (variant.price) price.textContent = variant.price;
      });

      this.root.querySelectorAll('[data-rplus-compare]').forEach((comparePrice) => {
        const hasComparePrice = variant.compareAtPrice > variant.rawPrice;
        comparePrice.toggleAttribute('hidden', !hasComparePrice);
        if (hasComparePrice && variant.comparePrice) comparePrice.textContent = variant.comparePrice;
      });

      this.root.querySelectorAll('[data-rplus-selected-variant]').forEach((selectedVariant) => {
        selectedVariant.textContent = variant.title;
      });

      this.root.querySelectorAll('[data-rplus-submit]').forEach((button) => {
        const label = button.querySelector('[data-rplus-submit-label]');
        const addLabel = button.dataset.addLabel || this.root.dataset.addLabel || 'Agregar al carrito';
        const soldOutLabel = button.dataset.soldOutLabel || this.root.dataset.soldOutLabel || 'Agotado';

        button.disabled = !variant.available;
        button.setAttribute('aria-disabled', String(!variant.available));

        if (label) {
          label.textContent = variant.available && variant.price
            ? `${addLabel} · ${variant.price}`
            : variant.available
              ? addLabel
              : soldOutLabel;
        }
      });

      this.root.querySelectorAll('[data-rplus-availability]').forEach((status) => {
        const availableLabel = status.dataset.availableLabel || 'Disponible';
        const soldOutLabel = status.dataset.soldOutLabel || 'Agotado';
        status.textContent = variant.available ? availableLabel : soldOutLabel;
        status.classList.toggle('is-unavailable', !variant.available);
      });

      if (variant.image && this.root.dataset.customProductImage !== 'true') {
        this.root.querySelectorAll('[data-rplus-product-image]').forEach((image) => {
          image.src = variant.image;
          image.srcset = this.buildResponsiveSrcset(variant.image);
          image.sizes = '(min-width: 990px) 38vw, 92vw';
          image.alt = variant.imageAlt;
        });
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState(window.history.state, '', url);
    }

    buildResponsiveSrcset(source) {
      const widths = [420, 640, 840, 1100, 1400];

      try {
        return widths.map((width) => {
          const imageUrl = new URL(source, window.location.origin);
          imageUrl.searchParams.set('width', String(width));
          return `${imageUrl.toString()} ${width}w`;
        }).join(', ');
      } catch (_error) {
        return '';
      }
    }

    bindBuyLinks() {
      this.root.querySelectorAll('[data-rplus-scroll-to-buy]').forEach((link) => {
        link.addEventListener('click', (event) => {
          if (!this.buy) return;

          event.preventDefault();
          const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.buy.scrollIntoView({
            behavior: reducedMotion ? 'auto' : 'smooth',
            block: 'center',
          });

          const focusTarget = this.buy.querySelector(
            '[data-rplus-submit]:not(:disabled), select:not(:disabled), a[href], button:not(:disabled)'
          ) || this.buy;
          window.setTimeout(() => focusTarget.focus({ preventScroll: true }), reducedMotion ? 0 : 450);
        });
      });
    }

    setupSticky() {
      if (!this.hero || !this.sticky) return;

      this.heroObserver = new IntersectionObserver(
        (entries) => {
          this.heroVisible = entries[0]?.isIntersecting ?? true;
          this.updateSticky();
        },
        { threshold: 0.04 }
      );
      this.heroObserver.observe(this.hero);

      if (this.stickyStop) {
        this.stopObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            this.stopVisible = Boolean(entry?.isIntersecting || (entry && entry.boundingClientRect.top < 0));
            this.updateSticky();
          },
          { threshold: 0.02 }
        );
        this.stopObserver.observe(this.stickyStop);
      }

      this.mediaChangeHandler = () => this.updateSticky();
      this.mobileMedia.addEventListener?.('change', this.mediaChangeHandler);
      this.updateSticky();
    }

    updateSticky() {
      if (!this.sticky) return;
      const shouldShow = this.mobileMedia.matches && !this.heroVisible && !this.stopVisible;
      this.sticky.toggleAttribute('hidden', !shouldShow);
      this.sticky.setAttribute('aria-hidden', String(!shouldShow));
    }

    destroy() {
      this.heroObserver?.disconnect();
      this.stopObserver?.disconnect();
      this.mobileMedia.removeEventListener?.('change', this.mediaChangeHandler);
      delete this.root.dataset.rplusReady;
    }
  }

  const instances = new WeakMap();

  const init = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new RevibrasPlus(root));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  });
})();
